import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { phoneDefaults } from '@/api/phoneDefaults';
import type { PlatformPhoneDefaultsView } from '@/api/phoneDefaults';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { CountryPrefixSelect } from '@/components/PhoneInput';
import { CheckCircle2, Globe } from 'lucide-react';

/**
 * Super-admin page for the platform default country code.
 *
 * Every phone field in the portal pre-fills its country prefix from a
 * 4-tier resolution: customer override → reseller override → THIS platform
 * setting → '+1'. Resellers and customers set their overrides on their own
 * detail pages; this page owns the platform-wide fallback.
 *
 * Mirrors the CNAM settings page shape (status card + edit card).
 */
export function PhoneDefaultsSettings() {
  const qc = useQueryClient();
  const [current, setCurrent] = useState<PlatformPhoneDefaultsView | null>(null);
  const [draft, setDraft] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let ignore = false;
    phoneDefaults.getPlatform().then((v) => {
      if (ignore) return;
      setCurrent(v);
      setDraft(v.country_code);
    }).catch(() => { /* leave defaults */ });
    return () => { ignore = true; };
  }, []);

  async function save() {
    if (!draft) return;
    setStatus(null);
    setSaving(true);
    try {
      await phoneDefaults.putPlatform({ country_code: draft });
      setStatus('Saved. New phone fields across the portal now pre-fill this prefix.');
      const next = await phoneDefaults.getPlatform();
      setCurrent(next);
      setDraft(next.country_code);
      qc.invalidateQueries({ queryKey: ['phoneDefaults'] });
    } catch (err) {
      setStatus(err instanceof Error ? err.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  async function reset() {
    setStatus(null);
    setSaving(true);
    try {
      await phoneDefaults.putPlatform({ clear: true });
      setStatus('Reset to the built-in default (+1 United States).');
      const next = await phoneDefaults.getPlatform();
      setCurrent(next);
      setDraft(next.country_code);
      qc.invalidateQueries({ queryKey: ['phoneDefaults'] });
    } catch (err) {
      setStatus(err instanceof Error ? err.message : 'Reset failed.');
    } finally {
      setSaving(false);
    }
  }

  const dirty = current !== null && draft !== null && draft !== current.country_code;

  return (
    <>
      <PageHeader
        eyebrow="Settings"
        title="Default country code"
        description="The dialing prefix phone fields pre-fill platform-wide. Resellers and customers can override it on their own pages; anyone typing a number can still switch the prefix per field."
      />
      <div className="max-w-2xl space-y-6">
        <Card className="border-emerald-200/60">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-medium text-foreground">
                  Platform default: <span className="font-mono" dir="ltr">{current?.country_code ?? '+1'}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {current?.configured
                    ? 'Explicitly configured. Applies wherever no reseller or customer override exists.'
                    : 'Built-in default (+1 United States). Applies wherever no reseller or customer override exists.'}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Globe className="h-4 w-4 text-muted-foreground" />
              Platform default prefix
            </CardTitle>
            <CardDescription>
              Operators type national numbers everywhere in the portal — this prefix is
              prepended automatically. Numbers entered with a full +country code are
              never rewritten.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1 max-w-sm">
              <Label htmlFor="platform-country-code">Country</Label>
              <CountryPrefixSelect
                id="platform-country-code"
                value={draft}
                onChange={(v) => setDraft(v)}
                disabled={saving}
              />
              <p className="text-xs text-muted-foreground">
                Resolution order: customer override → reseller override → this setting → +1.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              <Button onClick={save} disabled={saving || !dirty || !draft}>
                {saving ? 'Saving…' : 'Save default'}
              </Button>
              {current?.configured && (
                <Button variant="outline" onClick={reset} disabled={saving}>
                  Reset to +1
                </Button>
              )}
            </div>
            {status && <p role="alert" className="text-sm">{status}</p>}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
